import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building, Users, ShieldAlert, IndianRupee, Activity, Stethoscope, Pill } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { KpiCard, PageHeader, Loading, ErrorState } from "@/components/Bits";
import { inrShort, num } from "@/lib/format";

export default function OwnerDashboard() {
  const [stats, setStats] = useState(null);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/owner/stats").then(({ data }) => setStats(data)).catch((e) => setError(apiError(e)));
  }, []);

  if (error) return <ErrorState message={error} />;
  if (!stats) return <Loading label="Loading platform statistics…" />;

  const cards = [
    { label: "Total Businesses", value: num(stats.total_businesses), to: "/owner/businesses", icon: Building, testid: "kpi-total-businesses" },
    { label: "Active Businesses", value: num(stats.active_businesses), to: "/owner/businesses?status=active", icon: Activity, tone: "success", testid: "kpi-active-businesses" },
    { label: "Hospitals", value: num(stats.hospitals), to: "/owner/businesses?type=hospital", icon: Stethoscope, testid: "kpi-hospitals" },
    { label: "Pharmacies", value: num(stats.pharmacies), to: "/owner/businesses?type=pharmacy", icon: Pill, testid: "kpi-pharmacies" },
    { label: "Hospital + Pharmacy", value: num(stats.hybrid), to: "/owner/businesses?type=hospital_pharmacy", icon: Building, testid: "kpi-hybrid" },
    { label: "Platform Users", value: num(stats.platform_users), to: "/owner/users", icon: Users, testid: "kpi-platform-users" },
    { label: "Total Staff", value: num(stats.total_staff), to: "/owner/users", icon: Users, testid: "kpi-total-staff" },
    { label: "Active Admins", value: num(stats.active_admins), to: "/owner/users?role=admins", icon: Users, testid: "kpi-active-admins" },
    { label: "Suspended Accounts", value: num(stats.suspended_accounts), to: "/owner/users?status=suspended", icon: ShieldAlert, tone: "danger", testid: "kpi-suspended-accounts" },
    { label: "Platform Revenue", value: inrShort(stats.revenue), to: "/owner/businesses", icon: IndianRupee, tone: "accent", testid: "kpi-revenue" },
    { label: "Patients", value: num(stats.total_patients), to: "/owner/businesses", icon: Users, testid: "kpi-patients" },
    { label: "Visits (OPD + IPD)", value: num(stats.total_visits), to: "/owner/businesses", icon: Activity, testid: "kpi-visits" },
  ];

  return (
    <div data-testid="owner-dashboard">
      <PageHeader overline="Platform owner" title="Platform overview"
        subtitle="Live counts across every business on AJ Classic Healthcare. Each card opens the records behind the number."
        actions={
          <button onClick={() => navigate("/owner/businesses?new=1")} data-testid="owner-add-business-btn"
            className="rounded-full bg-[#0F172A] px-6 py-2.5 text-sm font-medium text-white hover:bg-[#1E293B] hover:text-white">
            Add business
          </button>
        } />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {cards.map((c) => <KpiCard key={c.label} {...c} />)}
      </div>
      <div className="mt-6 grid gap-4 sm:grid-cols-3">
        <KpiCard label="Suspended Businesses" value={num(stats.suspended_businesses)} tone="danger"
          to="/owner/businesses?status=suspended" testid="kpi-suspended-businesses" />
        <KpiCard label="Audit Events" value="View log" to="/owner/audit" testid="kpi-audit" />
        <KpiCard label="Business Directory" value="Manage" to="/owner/businesses" testid="kpi-directory" />
      </div>
    </div>
  );
}
